import type { V2CreateAdminSocietyInput } from '@idc-forms/schemas/api-v2/admin-societies'
import {
  CircleOff,
  CirclePlay,
  FolderOpen,
  Images,
  Info,
  KeyRound,
  Pencil,
  Trash2
} from 'lucide-react'
import { useState } from 'react'
import { DocumentPortfolioTab } from '@/features/documents/components/document-portfolio-tab'
import { GalleryTabWithCopy } from '@/features/documents/components/gallery-tab-with-copy'
import { Permission } from '@/features/roles/consts/permissions'
import { DetailFieldRow } from '@/shared/components/detail-rows/detail-field-row'
import { UserChip } from '@/shared/components/detail-rows/user-chip'
import { ScrollFade } from '@/shared/components/scroll-fade/scroll-fade'
import { SidePanelActions } from '@/shared/components/side-panel/side-panel-actions'
import {
  SidePanelSection,
  SidePanelSectionBody
} from '@/shared/components/side-panel/side-panel-blocks'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { cn } from '@/shared/lib/utils'
import { useSidePanelStore } from '@/shared/store/side-panel.store'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/shared/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { formatHumanDateTime } from '@/shared/utils/format-date'
import {
  useDeleteSocietyV2Mutation,
  useUpdateSocietyV2Mutation
} from '../hooks/use-society-v2-mutations'
import type { SocietyV2AdminRow } from '../models/society-admin-v2'
import { SocietyActiveBadge } from './societies-admin-columns'
import { SocietyV2Form } from './society-v2-form'

type SocietyV2DetailPanelProps = {
  society: SocietyV2AdminRow
}

/**
 * Píldora del estado de credenciales SAP. La API nunca devuelve las credenciales
 * en sí, sólo el flag `sapConfigured`: acá se traduce a "Configuradas / Sin
 * configurar" con el mismo lenguaje visual sobrio del badge de estado.
 */
function SapCredentialsBadge({ configured }: { configured: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          configured ? 'bg-emerald-500' : 'bg-amber-500'
        )}
      />
      {configured ? 'Configuradas' : 'Sin configurar'}
    </span>
  )
}

/**
 * Panel de detalle de la sociedad contra la API v2. Espeja el panel v1 —mismo
 * `SocietyV2Form`, mismo badge de estado, mismo acordeón de actividad— y sólo
 * cambia el transporte: direcciona por `soc_`, la empresa madre llega como
 * `{ id: cmp_, name }` y la autoría ya resuelta a `usr_`.
 *
 * Las credenciales SAP nunca vuelven de la API: el detalle muestra si están
 * configuradas (`sapConfigured`), y la edición las deja vacías salvo que el
 * usuario las re-escriba (vacío = conservar).
 */
export function SocietyV2DetailPanel({ society }: SocietyV2DetailPanelProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const closePanel = useSidePanelStore((s) => s.closePanel)
  const updateMutation = useUpdateSocietyV2Mutation()
  const deleteMutation = useDeleteSocietyV2Mutation()
  const { has } = usePermissions()

  const toggleActive = () => {
    updateMutation.mutate({ id: society.id, body: { active: !society.active } })
  }

  if (mode === 'edit') {
    return (
      <SocietyV2Form
        mode="edit"
        defaultValues={{
          name: society.name,
          // El SDK tipa el id de empresa como string plano; el form usa el
          // branded `cmp_` del schema. Mismo dato en runtime.
          company: society.company?.id as V2CreateAdminSocietyInput['company'] | undefined,
          active: society.active
        }}
        submitLabel="Guardar cambios"
        pending={updateMutation.isPending}
        onSubmit={(values) => {
          // Credenciales vacías = conservar: se quitan del payload antes del PATCH
          // (el backend también trata '' como "conservar", pero no las mandamos).
          const { dbName, dbUser, dbPassword, ...rest } = values
          const body = {
            ...rest,
            ...(dbName ? { dbName } : {}),
            ...(dbUser ? { dbUser } : {}),
            ...(dbPassword ? { dbPassword } : {})
          }
          updateMutation.mutate(
            { id: society.id, body },
            {
              onSuccess: () => {
                setMode('view')
              }
            }
          )
        }}
        onCancel={() => {
          setMode('view')
        }}
      />
    )
  }

  return (
    <div className="flex flex-col">
      <SidePanelSection>
        <SidePanelSectionBody className="gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold leading-tight">{society.name}</h3>
            {society.company && (
              <p className="truncate text-sm text-muted-foreground">{society.company.name}</p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <DetailFieldRow label="Estado" value={<SocietyActiveBadge active={society.active} />} />
            <DetailFieldRow label="Empresa" value={society.company?.name ?? '—'} />
            <DetailFieldRow
              label="Credenciales SAP"
              value={<SapCredentialsBadge configured={society.sapConfigured} />}
            />
          </div>
        </SidePanelSectionBody>
      </SidePanelSection>

      <SidePanelSection>
        <SidePanelActions
          maxInline={2}
          actions={[
            ...(has(Permission.UpdateSocieties)
              ? [
                  {
                    label: 'Editar sociedad',
                    icon: <Pencil className="h-4 w-4" />,
                    variant: 'default' as const,
                    primary: true,
                    onClick: () => {
                      setMode('edit')
                    }
                  },
                  {
                    label: society.active ? 'Desactivar sociedad' : 'Activar sociedad',
                    icon: society.active ? (
                      <CircleOff className="h-4 w-4" />
                    ) : (
                      <CirclePlay className="h-4 w-4" />
                    ),
                    variant: 'outline' as const,
                    disabled: updateMutation.isPending,
                    onClick: toggleActive
                  }
                ]
              : []),
            ...(has(Permission.DeleteSocieties)
              ? [
                  {
                    label: 'Eliminar sociedad',
                    icon: <Trash2 className="h-4 w-4" />,
                    variant: 'destructive' as const,
                    disabled: deleteMutation.isPending,
                    onClick: () => {
                      setConfirmDelete(true)
                    }
                  }
                ]
              : [])
          ]}
        />
      </SidePanelSection>

      <Tabs defaultValue="more-info">
        {/*
          `ScrollFade` alrededor de la `TabsList`, como ya lo tiene el subtipo. No es adorno:
          estas pestañas viven en un panel que se arrastra hasta 320 px, y ahí tres pestañas no
          entran. Sin esto la última se RECORTA sin ninguna señal de que hay más — el usuario ve
          dos pestañas y concluye que son dos. El degradado del borde dice que sigue, y el
          `w-max min-w-full` deja que la lista crezca en vez de comprimir los rótulos.
        */}
        <ScrollFade className="w-full" scrollClassName="overflow-y-hidden">
          <TabsList className="flex h-auto w-max min-w-full justify-normal rounded-none">
            <TabsTrigger className="flex gap-1" value="more-info">
              <Info className="size-4" /> Más información
            </TabsTrigger>
            <TabsTrigger className="flex gap-1" value="portfolio">
              <FolderOpen className="size-4" /> Cartapacio
            </TabsTrigger>

            {/* La galería va aparte del cartapacio porque el trabajo es otro: ahí se
                clasifica una lista de papeles, acá se decide qué se ve y en qué orden. */}
            <TabsTrigger className="flex gap-1" value="gallery">
              <Images className="size-4" /> Galería
            </TabsTrigger>
          </TabsList>
        </ScrollFade>
        <TabsContent value="more-info">
          <Accordion type="multiple" defaultValue={['credentials', 'activity']}>
            <AccordionItem value="credentials">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Conexión SAP
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow
                    label="Credenciales"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                        <SapCredentialsBadge configured={society.sapConfigured} />
                      </span>
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Las credenciales se guardan encriptadas y nunca se muestran. Edita la sociedad
                    para reemplazarlas.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="activity">
              <AccordionTrigger className="px-3 border-b border-transparent data-[state=open]:border-border">
                Actividad
              </AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-col gap-2 pt-2 pl-3 pr-2">
                  <DetailFieldRow label="Creado" value={formatHumanDateTime(society.createdAt)} />
                  <DetailFieldRow
                    label="Creado por"
                    value={<UserChip name={society.createdBy?.fullname} />}
                  />
                  <DetailFieldRow
                    label="Actualizado"
                    value={formatHumanDateTime(society.updatedAt)}
                  />
                  <DetailFieldRow
                    label="Actualizado por"
                    value={<UserChip name={society.updatedBy?.fullname} />}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </TabsContent>
        <TabsContent value="portfolio">
          <DocumentPortfolioTab ownerType="society" ownerId={society.id} />
        </TabsContent>
        <TabsContent value="gallery">
          <GalleryTabWithCopy ownerType="society" ownerId={society.id} ownerLabel={society.name} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar sociedad</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará “{society.name}”. Los proyectos históricos que la referencian se
              conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(society.id, {
                  onSuccess: () => {
                    closePanel()
                  }
                })
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
